function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
    this.querySelector(".quantity__button[name='minus']").classList.toggle('disabled', false);
    this.querySelector(".quantity__button[name='plus']").classList.toggle('disabled', false);
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      // console.log(this.input, this.input.value, "clicked! plus", 'step: ', parseInt(this.input.step), 'min: ', this.input.dataset.min)
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        // console.log(this.input.value, "this.input.value")
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      // console.log(this.input, this.input.value, "clicked! minus")
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    const buttonMinus = this.querySelector(".quantity__button[name='minus']");
    const buttonPlus = this.querySelector(".quantity__button[name='plus']");
    buttonMinus.classList.remove('disabled');
    buttonPlus.classList.remove('disabled');
    if (this.input.min) {
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  console.log(selector);
  if (!value || !selector.options) return;
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    if (this.querySelector('.all-authors__filter--button-close')) {
      this.querySelector('.all-authors__filter--button-close').addEventListener('click', this.hide.bind(this, false));
    }
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

const slickPrevArrow = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.0875 13.825L9.27084 10L13.0875 6.175L11.9125 5L6.91251 10L11.9125 15L13.0875 13.825Z" fill="#111827"/></svg>';
// const slickPrevArrow = '<svg width="16" height="22" viewBox="0 0 16 22" fill="none" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="evenodd" clip-rule="evenodd" d="M3 11.0004C3 10.6591 3.13999 10.3177 3.41856 10.0577L10.5612 3.39106C11.1197 2.86973 12.0225 2.86973 12.5811 3.39106C13.1396 3.91239 13.1396 4.75506 12.5811 5.27639L6.44845 11.0004L12.5811 16.7244C13.1396 17.2457 13.1396 18.0884 12.5811 18.6097C12.0225 19.1311 11.1197 19.1311 10.5612 18.6097L3.41856 11.9431C3.13999 11.6831 3 11.3417 3 11.0004Z" fill="#1E1F1F" fill-opacity="1"/></svg>';
const slickNextArrow = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.91248 13.825L10.7291 10L6.91248 6.175L8.08748 5L13.0875 10L8.08748 15L6.91248 13.825Z" fill="#111827"/></svg>';
// const slickNextArrow = '<svg width="16" height="22" viewBox="0 0 16 22" fill="none" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="evenodd" clip-rule="evenodd" d="M13 11.0003C13 11.3417 12.86 11.683 12.5814 11.943L5.43884 18.6097C4.88029 19.131 3.97747 19.131 3.41891 18.6097C2.86036 18.0883 2.86036 17.2457 3.41891 16.7243L9.55155 11.0003L3.41891 5.27634C2.86036 4.75501 2.86036 3.91234 3.41891 3.39101C3.97747 2.86967 4.88029 2.86967 5.43884 3.39101L12.5814 10.0577C12.86 10.3177 13 10.659 13 11.0003Z" fill="#1E1F1F"/> </svg>';

const main_slider = document.querySelector('.main-slider');
if (main_slider) {
  $('.main-slider').slick({
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    dots: false,
    // asNavFor: '.thumbnails-slider',
    prevArrow: '<button type="button" class="slick-prev slick-arrow">' + slickPrevArrow + '</button>',
    nextArrow: '<button type="button" class="slick-next slick-arrow">' + slickNextArrow + '</button>',
    responsive: [
      {
        breakpoint: 560,
        settings: {
          slidesToShow: 1,
          centerPadding: '10px',
          adaptiveHeight: true,
          arrows: false
        }
      }
    ]
  });
  // On after slide change
  $('.main-slider').on('afterChange', function (event, slick, currentSlide) {
    console.log(currentSlide);
    $('.thumbnails-grid .thumb_grid').removeClass('grid-current');
    $('.thumbnails-grid .thumb_grid[data-slickIndex="' + currentSlide + '"]').addClass('grid-current');
  });
  $('.thumbnails-grid .thumb_grid').on('click', function (event) {
    $('.thumbnails-grid .thumb_grid').removeClass('grid-current');
    $(this).addClass('grid-current');
    $('.main-slider').slick('slickGoTo', $(this).attr('data-slickIndex'));
    console.log($(this).data('slickIndex'));
    console.log($(this).attr('data-slickIndex'));
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const pos = event?.data?.variant?.featured_image?.position;
      if (!pos) return;
      $('.main-slider').slick('slickGoTo', pos - 1);
    });
  }
}
const slider = document.querySelectorAll('.slider-element');
const mobileSliderQuery = window.matchMedia('(max-width: 559px)');
if (slider) {
  slider.forEach((item) => {
    if (!item.classList.contains('slick-initialized')) {
      if (item.classList.contains('slideshow')) {
        $(item).slick({
          infinite: true,
          dots: true,
          slidesToShow: 1,
          slidesToScroll: 1,
          // adaptiveHeight: true,
          speed: 500,
          fade: true,
          prevArrow: '<button type="button" class="slick-prev slick-arrow">' + slickPrevArrow + '</button>',
          nextArrow: '<button type="button" class="slick-next slick-arrow">' + slickNextArrow + '</button>',
          responsive: [
            {
              breakpoint: 769,
              settings: {
                slidesToShow: 1,
                centerPadding: '10px',
                // adaptiveHeight: true,
                arrows: false
              }
            }
          ]
        });
      } else {
        let slidesToShowMobile = 1;
        let arrowsMobile = true;
        if (item.classList.contains('products-list__products')) {
          slidesToShowMobile = 1.25;
          arrowsMobile = false;
        }
        const options = {
          infinite: true,
          slidesToShow: 4,
          slidesToScroll: 1,
          centerPadding: '30px',
          prevArrow: '<button type="button" class="slick-prev slick-arrow">' + slickPrevArrow + '</button>',
          nextArrow: '<button type="button" class="slick-next slick-arrow">' + slickNextArrow + '</button>',
          responsive: [
            {
              breakpoint: 560,
              settings: {
                slidesToShow: slidesToShowMobile,
                centerPadding: '0px',
                arrows: arrowsMobile,
                centerMode: true,
                infinite: false
              }
            },
            {
              breakpoint: 769,
              settings: {
                slidesToShow: 2,
                centerPadding: '10px'
              }
            },
            {
              breakpoint: 989,
              settings: {
                slidesToShow: 3,
              }
            }
          ]
        };
        if (item.classList.contains('products-list__products')) {
          // Phones scroll book lists natively: Slick drops any swipe that drifts 4px vertically
          options.responsive[0].settings = 'unslick';
          // Slick unslicks itself below 560px but never re-inits, so widening has to restore it
          const initOnWide = () => {
            if (!mobileSliderQuery.matches && !item.classList.contains('slick-initialized')) $(item).slick(options);
          };
          initOnWide();
          mobileSliderQuery.addEventListener('change', initOnWide);
        } else {
          $(item).slick(options);
        }
      }
    }
  })
}

function book_series_slider() {
  const products_list_products = document.querySelectorAll('.products-list__products:not(.product__info-container, .no-slider-element)');
  if (products_list_products) {
    if ($(window).width() < 759) {
      products_list_products.forEach((item) => {
        if (!item.classList.contains('slider-element') && !item.classList.contains('product-grid')) {
          item.querySelectorAll('.grid__item').forEach((grid) => {
            grid.classList.remove('hidden');
          });
          if (!item.className.includes('slick-initialized')) {
            $(item).slick({
              infinite: false,
              slidesToShow: 1.25,
              slidesToScroll: 1,
              centerPadding: '0px',
              arrows: false,
              centerMode: true,
              prevArrow: '<button type="button" class="slick-prev slick-arrow">' + slickPrevArrow + '</button>',
              nextArrow: '<button type="button" class="slick-next slick-arrow">' + slickNextArrow + '</button>'
            });
            $(item).slick('setPosition');
          } else {
            $(item).slick('setPosition');
          }
          setTimeout(() => {
            $(item).slick('setPosition');
          }, 1000)
        }
      })
    }
  }
}
book_series_slider();
window.addEventListener('resize', function () {
  const currentWidth = window.innerWidth;
  // console.log("Window resized to: " + currentWidth + "px");
  book_series_slider();
});


document.addEventListener('DOMContentLoaded', function () {
  const gridAtc = document.querySelectorAll('.products-list__product-atc');
  const quickViewPopup = document.getElementById('quick-view-popup');
  const quickViewPopupWrap = document.getElementById('quick-view-content__wrap');
  const closeQuickView = document.getElementById('close-quick-view');
  const quickViewForm = document.getElementById('quick-view-form');
  const variantDropdown = document.getElementById('variant-dropdown');
  const quickViewPrice = document.getElementById('quick-view-price');
  const atcPopup = document.querySelector('.quick-view-popup');
  const popupSlider = document.querySelector('.quick-view-popup .large-image');
  const openImageModal = document.querySelector('.open-image-modal');
  const ImageModal = document.getElementById('image-modal');
  const ImageModalWrap = document.querySelector('.image-modal__content');
  const ImageModalClose = document.querySelector('.image-modal__close');
  const ImageModalSlider = document.querySelector('.images-modal-slider');
  const ImagesPDP = document.querySelectorAll('.product-images--all--img');
  const filterWrap = document.querySelectorAll('.popup-overlay');
  const WrapPDPMain = document.querySelector('.product-images--main-img');
  const ImagesPDPMain = document.querySelector('.product-images--main-img img');
  const pdpAtc = document.querySelector('.custom-product-template .atc-popup');
  const pdpQty = document.querySelector('.custom-product-template .quantity__input');
  const cartQty = document.querySelectorAll('.cart-item .quantity__input');
  const cartDrawer = document.querySelector('cart-drawer.drawer');
  const cartDrawerCustom = document.getElementById('mini-cart__wrap');
  const viewBook = document.querySelector('.view-book');
  const pdpTabs = document.querySelectorAll('.custom-product-template .product-info__tabs--details');
  const pdpSmallImages = document.querySelector('.product-images--all');
  const closeCartDrawerOutside = document.querySelector('.close-background');
  const products_list__product__title = document.querySelectorAll('.products-list__product--url');
  const readMore = document.querySelectorAll('.read-more-btn');

  let ImageModalSliderImages;

  if (quickViewPopupWrap) {
    // Quick view markup is copied from <template>, so Judge.me has not set up its badge yet
    new MutationObserver(() => window.jdgm?.customizeBadges?.()).observe(quickViewPopupWrap, { childList: true });
  }

  if (closeCartDrawerOutside) {
    closeCartDrawerOutside.addEventListener('click', () => {
      if (cartDrawerCustom) {
        cartDrawerCustom.classList.remove('open')
        document.body.classList.remove('overflow-hidden');
        document.querySelector('html').classList.remove('overflow-hidden');
      }
    })
  }

  if (pdpQty) {
    pdpQty.addEventListener('input', function () {
      const maxValue = parseInt(this.getAttribute('data-max'), 10);
      let outOfStockError = document.querySelector('.out-of-stock-counter');

      if (parseInt(this.value, 10) > maxValue) {
        this.value = maxValue;
        if (outOfStockError) {
          outOfStockError.innerHTML = "Only " + maxValue + " in stock"

          setTimeout(() => {
            outOfStockError.classList.add('hidden')
          }, 3000)
        }
      }
    });
  }

  const container = document.querySelector('body'); // Or a more specific common ancestor

  container.addEventListener('input', (event) => {
    if (event.target.classList.contains('quantity__input')) {
      const input = event.target;
      const max = parseInt(input.getAttribute('max'), 10);
      let value = parseInt(input.value, 10);

      if (value > max) {
        input.value = max;
      }

      const outOfStockCounter = input.nextElementSibling;
      if (outOfStockCounter && outOfStockCounter.classList.contains('out-of-stock-counter')) {
        outOfStockCounter.textContent = `Only ${max} in stock`;
      }
    }
  });


  if (cartQty) {
    cartQty.forEach((item) => {
      item.addEventListener('change', () => {
        let wrap = item.closest('.cart-item__quantity-wrapper')
        let mess = wrap.querySelector('.cart-error-message')
        let varId = item.getAttribute('data-quantity-variant-id')
        let max = Number(item.getAttribute('data-max'));
        let qty = Number(item.value);
        let enable = true;
        if (qty > max) {
          enable = false;
          qty = max;
          if (mess) {
            mess.innerHTML = "Only " + max + " in stock";
            item.value = max;

            setTimeout(() => {
              mess.innerHTML = "";
            }, 3000)
          }
        }
        if (enable) {
          updateCartByVariantId(varId, qty);
        }
      })
    })
  }

  if (pdpAtc) {
    pdpAtc.addEventListener('click', () => {
      let id = pdpAtc.dataset.variantid;
      let qty = pdpQty.value;
      let maxqty = pdpQty.dataset.maxqty;

      addToCart(id, qty, maxqty, 'pdpAtc');
    })
  }

  if (ImagesPDP) {
    ImagesPDP.forEach((item) => {
      item.addEventListener('click', () => {
        ImagesPDP.forEach((itemm) => {
          itemm.classList.remove('active-image')
        })
        let img = item.querySelector('img').getAttribute('src');
        let img_loop = item.getAttribute('img-loop');
        item.classList.add('active-image');
        ImagesPDPMain.setAttribute('src', img);
        ImagesPDPMain.closest('.product-images--main-img').setAttribute('img-loop', img_loop);
      })
    })
  }

  if (ImageModalClose) {
    ImageModalClose.addEventListener('click', () => {
      document.body.classList.remove('overflow-hidden')
      if (ImageModal) {
        ImageModal.classList.remove('active')
      }
      if (ImageModalWrap) {
        ImageModalWrap.innerHTML = ""
        ImageModalWrap.classList.remove('slick-initialized')
        ImageModalWrap.classList.remove('slick-slider')
        ImageModalWrap.classList.remove('slick-dotted')
      }
    })
  }
  if (ImageModalSlider) {
    ImageModalSliderImages = ImageModalSlider.innerHTML;
  }

  if (openImageModal) {
    openImageModal.addEventListener('click', () => {
      document.body.classList.add('overflow-hidden')
      if (ImageModal) {
        ImageModal.classList.add('active')
      }

      if (ImageModalWrap) {
        ImageModalWrap.innerHTML = ImageModalSliderImages;
        if (ImageModalWrap) {
          if (!ImageModalWrap.classList.contains('slick-initialized')) {
            $(ImageModalWrap).slick({
              infinite: true,
              slidesToShow: 1,
              slidesToScroll: 1,
              dots: true,
              prevArrow: '<button type="button" class="slick-prev slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
                '<path d="M0.646444 3.64742C0.451181 3.84269 0.451181 4.15927 0.646444 4.35453L3.82842 7.53651C4.02369 7.73177 4.34027 7.73177 4.53553 7.53651C4.73079 7.34125 4.73079 7.02467 4.53553 6.8294L1.7071 4.00098L4.53553 1.17255C4.73079 0.977287 4.73079 0.660704 4.53553 0.465442C4.34027 0.27018 4.02369 0.27018 3.82842 0.465442L0.646444 3.64742ZM21 3.50098L0.999998 3.50098V4.50098L21 4.50098V3.50098Z" fill="black"/>\n' +
                '</svg>\n</button>',
              nextArrow: '<button type="button" class="slick-next slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
                '<path d="M20.3536 4.35355C20.5488 4.15829 20.5488 3.84171 20.3536 3.64645L17.1716 0.464466C16.9763 0.269204 16.6597 0.269204 16.4645 0.464466C16.2692 0.659728 16.2692 0.976311 16.4645 1.17157L19.2929 4L16.4645 6.82843C16.2692 7.02369 16.2692 7.34027 16.4645 7.53553C16.6597 7.7308 16.9763 7.7308 17.1716 7.53553L20.3536 4.35355ZM0 4.5H20V3.5H0V4.5Z" fill="black"/>\n' +
                '</svg>\n </button>',
              responsive: [
                {
                  breakpoint: 769,
                  settings: {
                    slidesToShow: 1,
                    centerPadding: '10px'
                  }
                }
              ]
            });
          }
        }
      }
    })
  }

  if (pdpTabs) {
    pdpTabs.forEach((item) => {
      item.addEventListener('click', () => {
        const curr = document.querySelectorAll('.custom-product-template .product-info-tab');

        pdpTabs.forEach((item) => {
          item.classList.remove('active');
        });
        curr.forEach((item) => {
          item.classList.remove('active');
        });
        item.classList.add('active');
        let current = item.dataset.menu;
        curr.forEach((item) => {
          if (item.dataset.block == current) {
            item.classList.add('active');
          }
        });
      })
    })
  }

  const parentElement = document.getElementById('quick-view-popup');
  const quickViewDetails = document.querySelectorAll('.product-info__tabs--details');
  if (parentElement) {
    parentElement.addEventListener('click', (event) => {
      if (event.target.classList.contains('product-info__tabs--details')) {
        const uu = parentElement.querySelectorAll('.product-info__tabs--details');
        const info = parentElement.querySelectorAll('.product-info-tab');
        uu.forEach((item) => {
          item.classList.remove('active');
        });
        info.forEach((item) => {
          item.classList.remove('active');
        });
        event.target.classList.add('active');
        let current = event.target.dataset.menu;
        info.forEach((item) => {
          if (item.dataset.block == current) {
            item.classList.add('active');
          }
        });
      }
    });
  }

  if (atcPopup) {
    atcPopup.addEventListener('click', (event) => {
      if (event.target.classList.contains('atc-popup')) {

        let id = event.target.dataset.variantid;
        let maxqty = event.target.dataset.maxqty;
        let qty_wrap = document.querySelector('.quick-view-popup .quantity__input');
        let qty;
        if (qty_wrap) {
          qty = qty_wrap.value;
        }

        addToCart(id, qty, maxqty, 'atcPopup');
        closeQuickView.click();
      }
    })
  }

  if (document.querySelector('.related-products')) {
    document.querySelector('.related-products').addEventListener('click', function (event) {
      if (event.target.closest('.products-list__product-atc')) {
        const item = event.target.closest('.products-list__product-atc');
        let id = item.dataset.variantid;
        let maxQty = item.dataset.maxqty;
        addToCart(id, 1, maxQty, 'releted-products');
      }
    });
  }

  if (document.querySelector('.additional-products-cart--items')) {
    document.querySelector('.additional-products-cart--items').addEventListener('click', function (event) {
      if (event.target.closest('.products-list__product-atc')) {
        const item = event.target.closest('.products-list__product-atc');
        let id = item.dataset.variantid;
        let maxQty = item.dataset.maxqty;
        addToCart(id, 1, maxQty, 'additional-products-cart--items');
      }
    });
  }

  // if (document.querySelectorAll('.products-list__products')) {
  //   document.querySelectorAll('.products-list__products').forEach(function (productList) {
  //     productList.addEventListener('click', function (event) {
  //       if (event.target.closest('.products-list__product-atc')) {
  //         // console.log('products-list__products');
  //         const item = event.target.closest('.products-list__product-atc');
  //         let id = item.dataset.variantid;
  //         let maxqty = item.dataset.maxqty;
  //         addToCart(id, 1, maxqty, 'products-list__products');
  //       }
  //     });
  //   });
  // }

  if (document.getElementById('ProductGridContainer')) {
    document.getElementById('ProductGridContainer').querySelector('.collection').addEventListener('click', function (event) {
      if (event.target.closest('.products-list__product-atc')) {
        if (event.__atcHandled) return;
        event.__atcHandled = true;
        // console.log('ProductGridContainer');
        const item = event.target.closest('.products-list__product-atc');
        let id = item.dataset.variantid;
        let maxqty = item.dataset.maxqty;
        addToCart(id, 1, maxqty, 'ProductGridContainer');
      }
    })
  }

  if (document.querySelectorAll('.product-card-wrapper')) {
    document.querySelectorAll('.product-card-wrapper').forEach((item) => {
      item.addEventListener('click', function (event) {
        if (event.target.closest('.products-list__product-atc')) {
          const item = event.target.closest('.products-list__product-atc');
          let id = item.dataset.variantid;
          let maxqty = item.dataset.maxqty;
          addToCart(id, 1, maxqty, 'product-card-wrapper');
        }
      });
    })
  }

  closeQuickView.addEventListener('click', function () {
    quickViewPopup.classList.add('hidden-smooth');
    document.body.classList.remove('overflow-hidden')
    document.querySelector('html').classList.remove('overflow-hidden')
  });

  function openQuickView(handle) {
    fetch('/products/' + handle + '.js')
      .then(response => response.json())
      .then(product => {
        document.getElementById('quick-view-large-image').src = product.images[0];
        document.getElementById('quick-view-title').innerText = product.title;
        document.getElementById('quick-view-vendor').innerText = product.vendor;
        quickViewPrice.innerText = Shopify.formatMoney(product.price).replace('.00', '');
        document.getElementById('quick-view-description').innerHTML = product.description;

        var thumbnailContainer = document.querySelector('.thumbnail-images');
        thumbnailContainer.innerHTML = '';
        product.images.forEach((image, index) => {
          if (index > 0) {
            var thumb = document.createElement('div');
            thumb.classList.add('thumbnail-image');
            thumb.innerHTML = `<img src="${image}" alt="">`;
            thumbnailContainer.appendChild(thumb);
          }
        });

        quickViewPopup.classList.remove('hidden-smooth');
      });
  }

  function addToCart(variantId, quantity, maxQty, eventTarget = null) {
    console.log(eventTarget);
    let send_to_cart = true;
    let product_in_cart = '';
    let product_id = '';
    for (let i = 0; i < window.productsInCart.length; i++) {
      if (window.productsInCart[i].id == variantId || window.productsInCart[i].variant_id == variantId) {
        if (window.productsInCart[i].id == variantId) {
          product_id = window.productsInCart[i].id;
        } else {
          product_id = window.productsInCart[i].variant_id;
        }
        product_in_cart = i;
        if (maxQty == '') {
          if (window.productsInCart[i].maxqty) {
            maxqty = window.productsInCart[i].maxqty;
          } else {
            maxqty = window.productsInCart[i].qty;
          }
          // maxqty = window.productsInCart[i].maxqty;
        }
        if (maxQty == undefined) {
          maxQty = 500;
        }

        let new_qty = window.productsInCart[i].qty + quantity;
        if (window.productsInCart[i].qty < maxQty && new_qty <= maxQty) {
          send_to_cart = true;
        } else {
          send_to_cart = false;
        }
      }
    }
    if (product_in_cart != '') {
      window.productsInCart[product_in_cart].qty = window.productsInCart[product_in_cart].qty + quantity;
    } else {
      var product_to_cart = {
        id: variantId,
        variant_id: variantId,
        qty: quantity,
        maxqty: maxQty
      };
      window.productsInCart.push(product_to_cart);
    }
    // console.log(window.productsInCart);
    // console.log(send_to_cart);
    console.log(quantity);
    if (send_to_cart) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: parseInt(quantity)
          }]
        })
      })
        .then(response => response.json())
        .then(data => {
          document.body.classList.add('overflow-hidden')
          document.querySelector('html').classList.add('overflow-hidden')
          if (window.ShopifyTemplate.name != 'cart') {
            window.refreshCart();
            cartDrawerCustom.classList.add('open');
          } else {
            location.reload()
          }
        })
    } else {
      let message;

      if (window.Shopify.locale == 'uk') {
        message = "Всього " + maxQty + " в наявності";
      } else if (window.Shopify.locale == 'ru') {
        message = "Всего " + maxQty + " в наличии";
      } else {
        message = "Only " + maxQty + " in stock";
      }

      const iteQtyMessages = document.querySelectorAll('.products-list__product--message.message-' + product_id);
      iteQtyMessages.forEach((item) => {
        item.innerHTML = message;

        item.classList.remove('d-none');
        setTimeout(() => {
          item.classList.add('d-none');
        }, 3000);
      });
    }
  }

  function removeFromCart(variantId) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 0
      })
    })
      .then(response => response.json())
      .then(data => {
        if (window.ShopifyTemplate.name != 'cart') {
          window.refreshCart();
          cartDrawerCustom.classList.add('open');
        } else {
          location.reload()
        }

      })
  }

  function updateCartByVariantId(variantId, quantity) {
    if (document.getElementById('mini-cart__wrap')) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {

          fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              updates: {
                [variantId]: parseInt(quantity)
              }
            })
          })
            .then(response => response.json())
            .then(data => {
              document.body.classList.add('overflow-hidden');
              document.querySelector('html').classList.add('overflow-hidden');
              if (window.ShopifyTemplate.name != 'cart') {
                window.refreshCart();
                cartDrawerCustom.classList.add('open');
              } else {
                location.reload();
              }
            });
        });
    }
  }


  function onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        cartDrawer.innerHTML = html.getElementById('CartDrawer').innerHTML;
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }


  function updateCartDrawer(cart) {
    document.querySelector('.cart-count').textContent = cart.item_count;
    const cartItemList = document.querySelector('.cart-items-list');
    cartItemList.innerHTML = ''; // Clear the current cart items
    cart.items.forEach(item => {
      cartItemList.innerHTML += `
      <li>${item.product_title} - ${item.quantity} x ${item.price}</li>
    `;
    });
  }

  function openCartDrawer() {
    document.querySelector('.cart-drawer').classList.add('open');
  }

  function closeCartDrawer() {
    document.querySelector('.cart-drawer').classList.remove('open');
  }

  const checkboxes = Array.from(document.querySelectorAll('.gift-checkbox-input'))
  if (checkboxes) {
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const checkedCheckboxes = checkboxes.filter(cb => cb.checked)
        if (checkbox.checked) {
          const id = checkbox.dataset.id;
          addToCart(id, 1)
        } else {
          const id = checkbox.dataset.id;
          removeFromCart(id)
        }
        if (checkedCheckboxes.length > 2) {
          const uncheckedCheckboxes = checkedCheckboxes.filter(cb => cb !== checkbox)
          const randomIndex = Math.floor(Math.random() * uncheckedCheckboxes.length)
          uncheckedCheckboxes[randomIndex].checked = false
        }
      })
    })
  }



  if (document.querySelector('.product-grid-container')) {
    document.querySelector('.product-grid-container').addEventListener('click', function (event) {
      if (event.target.closest('.quick-view-trigger')) {
        const trigger = event.target.closest('.quick-view-trigger');
        let cart = document.getElementById('mini-cart__wrap');
        let quickViewOverLay = trigger.classList.contains('ajax-cart-trigger');

        if (quickViewOverLay && cart) {
          cart.classList.remove('open');
        }

        event.preventDefault();
        var handle = trigger.getAttribute('data-product-handle');
        let content = document.querySelector('.card-grid-quick-view-content[data-prodid = "' + handle + '"]');
        quickViewPopupWrap.innerHTML = content.innerHTML;
        const overlay = document.createElement('div');
        overlay.classList.add('popup-overlay');
        quickViewPopupWrap.prepend(overlay);
        document.body.classList.add('overflow-hidden');
        document.querySelector('html').classList.add('overflow-hidden');

        const popupSlider = document.querySelector('.quick-view-popup .large-image');

        quickViewPopup.classList.remove('hidden-smooth');

        const readMorePopup = document.querySelectorAll('.quick-view-content .read-more-btn');

        if (readMorePopup) {
          readMorePopup.forEach((btn) => {
            let opys = btn.closest('.product-info__opys');
            if (opys) {
              opys.style.display = 'block';
              opys.style.width = '100%';
              // opys.style.overflowY = 'none';
              let wrapHeight = opys.offsetHeight;
              opys.style.visibility = 'hidden';
              opys.style.position = 'absolute';

              // let contentHeight = Array.from(opys.querySelectorAll('p')).reduce((total, p) => {
              //   return total + p.offsetHeight;
              // }, 0);
              let contentHeight = 0;
              opys.querySelectorAll('p').forEach((p) => {
                // console.log(p.offsetHeight);
                contentHeight = contentHeight + p.offsetHeight;
              });
              contentHeight = contentHeight - btn.offsetHeight;

              opys.style.display = '';
              opys.style.visibility = '';
              opys.style.position = '';
              opys.style.overflowY = '';
              opys.style.minHeight = '';
              opys.style.maxHeight = '';
              // console.log(wrapHeight);
              // console.log(contentHeight);

              if (contentHeight > wrapHeight) {
                btn.style.display = 'block';
                opys.style.overflowY = 'hidden';
              } else {
                btn.style.display = 'none';
              }

              btn.addEventListener('click', () => {
                // console.log(111)
                if (opys.classList.contains('read-more-mode')) {
                  opys.classList.remove('read-more-mode');
                  btn.innerHTML = window.translations.readMore;
                } else {
                  opys.classList.add('read-more-mode');
                  btn.innerHTML = window.translations.readLess;
                }
              });
            }
          });
        }


        setTimeout(() => {
          if (popupSlider && !popupSlider.classList.contains('slick-initialized')) {
            $(popupSlider).slick({
              infinite: true,
              slidesToShow: 1,
              slidesToScroll: 1,
              centerMode: false,
              dots: true,
              prevArrow: '<button type="button" class="slick-prev slick-arrow">' + slickPrevArrow + '</button>',
              nextArrow: '<button type="button" class="slick-next slick-arrow">' + slickNextArrow + '</button>',
              // prevArrow: '<button type="button" class="slick-prev slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.646444 3.64742C0.451181 3.84269 0.451181 4.15927 0.646444 4.35453L3.82842 7.53651C4.02369 7.73177 4.34027 7.73177 4.53553 7.53651C4.73079 7.34125 4.73079 7.02467 4.53553 6.8294L1.7071 4.00098L4.53553 1.17255C4.73079 0.977287 4.73079 0.660704 4.53553 0.465442C4.34027 0.27018 4.02369 0.27018 3.82842 0.465442L0.646444 3.64742ZM21 3.50098L0.999998 3.50098V4.50098L21 4.50098V3.50098Z" fill="black"/></svg></button>',
              // nextArrow: '<button type="button" class="slick-next slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.3536 4.35355C20.5488 4.15829 20.5488 3.84171 20.3536 3.64645L17.1716 0.464466C16.9763 0.269204 16.6597 0.269204 16.4645 0.464466C16.2692 0.659728 16.2692 0.976311 16.4645 1.17157L19.2929 4L16.4645 6.82843C16.2692 7.02369 16.2692 7.34027 16.4645 7.53553C16.6597 7.7308 16.9763 7.7308 17.1716 7.53553L20.3536 4.35355ZM0 4.5H20V3.5H0V4.5Z" fill="black"/></svg></button>',
              responsive: [
                {
                  breakpoint: 989,
                  settings: {
                    slidesToShow: 1,
                    arrows: false,
                    centerPadding: '10px'
                  }
                }
              ]
            });
          }
        }, 900);

        setTimeout(() => {
          overlay.classList.add('hidden');
        }, 1500);
      }
      if (event.target.closest('.products-list__product-atc')) {
        if (event.__atcHandled) return;
        event.__atcHandled = true;
        // console.log('product-grid-container');
        const item = event.target.closest('.products-list__product-atc');
        let id = item.dataset.variantid;
        let maxqty = item.dataset.maxqty;
        addToCart(id, 1, maxqty, 'product-grid-container');
      }
    });
  }


  if (document.querySelector('.related-products')) {
    document.querySelector('.related-products').addEventListener('click', function (event) {
      if (event.target.closest('.quick-view-trigger')) {
        const trigger = event.target.closest('.quick-view-trigger');
        let cart = document.getElementById('mini-cart__wrap');
        let quickViewOverLay = trigger.classList.contains('ajax-cart-trigger');

        if (quickViewOverLay && cart) {
          cart.classList.remove('open');
        }

        event.preventDefault();
        var handle = trigger.getAttribute('data-product-handle');
        let content = document.querySelector('.card-grid-quick-view-content[data-prodid = "' + handle + '"]');
        quickViewPopupWrap.innerHTML = content.innerHTML;
        const overlay = document.createElement('div');
        overlay.classList.add('popup-overlay');
        quickViewPopupWrap.prepend(overlay);
        document.body.classList.add('overflow-hidden');
        document.querySelector('html').classList.add('overflow-hidden');

        const popupSlider = document.querySelector('.quick-view-popup .large-image');

        quickViewPopup.classList.remove('hidden-smooth');

        const readMorePopup = document.querySelectorAll('.quick-view-content .read-more-btn');

        if (readMorePopup) {
          readMorePopup.forEach((item) => {
            let i = item.closest('.product-info__opys');
            if (i) {
              i.style.display = 'block';
              i.style.visibility = 'hidden';
              i.style.position = 'absolute';

              let contentHeight = Array.from(i.querySelectorAll('p')).reduce((total, p) => {
                // console.log(total, p.offsetHeight, p)
                return total + p.offsetHeight;
              }, 0);

              let wrapHeight = i.offsetHeight;

              // console.log(contentHeight, wrapHeight)

              i.style.display = '';
              i.style.visibility = '';
              i.style.position = '';

              if (contentHeight > wrapHeight) {
                item.style.display = 'block';
              } else {
                item.style.display = 'none';
              }

              item.addEventListener('click', () => {
                if (i.classList.contains('read-more-mode')) {
                  i.classList.remove('read-more-mode');
                  item.innerHTML = window.translations.readMore;
                } else {
                  i.classList.add('read-more-mode');
                  item.innerHTML = window.translations.readLess;
                }
              });
            }
          });
        }


        setTimeout(() => {
          if (popupSlider && !popupSlider.classList.contains('slick-initialized')) {
            $(popupSlider).slick({
              infinite: true,
              slidesToShow: 1,
              slidesToScroll: 1,
              centerMode: false,
              dots: true,
              prevArrow: '<button type="button" class="slick-prev slick-arrow">' + slickPrevArrow + '</button>',
              nextArrow: '<button type="button" class="slick-next slick-arrow">' + slickNextArrow + '</button>',
              // prevArrow: '<button type="button" class="slick-prev slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.646444 3.64742C0.451181 3.84269 0.451181 4.15927 0.646444 4.35453L3.82842 7.53651C4.02369 7.73177 4.34027 7.73177 4.53553 7.53651C4.73079 7.34125 4.73079 7.02467 4.53553 6.8294L1.7071 4.00098L4.53553 1.17255C4.73079 0.977287 4.73079 0.660704 4.53553 0.465442C4.34027 0.27018 4.02369 0.27018 3.82842 0.465442L0.646444 3.64742ZM21 3.50098L0.999998 3.50098V4.50098L21 4.50098V3.50098Z" fill="black"/></svg></button>',
              // nextArrow: '<button type="button" class="slick-next slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.3536 4.35355C20.5488 4.15829 20.5488 3.84171 20.3536 3.64645L17.1716 0.464466C16.9763 0.269204 16.6597 0.269204 16.4645 0.464466C16.2692 0.659728 16.2692 0.976311 16.4645 1.17157L19.2929 4L16.4645 6.82843C16.2692 7.02369 16.2692 7.34027 16.4645 7.53553C16.6597 7.7308 16.9763 7.7308 17.1716 7.53553L20.3536 4.35355ZM0 4.5H20V3.5H0V4.5Z" fill="black"/></svg></button>',
              responsive: [
                {
                  breakpoint: 989,
                  settings: {
                    slidesToShow: 1,
                    arrows: false,
                    centerPadding: '10px'
                  }
                }
              ]
            });
          }
        }, 900);

        setTimeout(() => {
          overlay.classList.add('hidden');
        }, 1500);
      }
    });
  }

  if (document.querySelectorAll('.products-list__products')) {
    document.querySelectorAll('.products-list__products').forEach(function (productList) {
      productList.addEventListener('click', function (event) {
        if (event.target.closest('.quick-view-trigger')) {
          const trigger = event.target.closest('.quick-view-trigger');
          let cart = document.getElementById('mini-cart__wrap');
          let quickViewOverLay = trigger.classList.contains('ajax-cart-trigger');

          if (quickViewOverLay && cart) {
            cart.classList.remove('open');
          }

          event.preventDefault();
          var handle = trigger.getAttribute('data-product-handle');
          let content = document.querySelector('.card-grid-quick-view-content[data-prodid = "' + handle + '"]');
          quickViewPopupWrap.innerHTML = content.innerHTML;
          const overlay = document.createElement('div');
          overlay.classList.add('popup-overlay');
          quickViewPopupWrap.prepend(overlay);
          document.body.classList.add('overflow-hidden');
          document.querySelector('html').classList.add('overflow-hidden');

          const popupSlider = document.querySelector('.quick-view-popup .large-image');

          quickViewPopup.classList.remove('hidden-smooth');

          const readMorePopup = document.querySelectorAll('.quick-view-content .read-more-btn');

          if (readMorePopup) {
            readMorePopup.forEach((item) => {
              let i = item.closest('.product-info__opys');
              if (i) {
                // Temporarily show the element to measure its content
                i.style.display = 'block';
                i.style.visibility = 'hidden';
                i.style.position = 'absolute';

                let contentHeight = Array.from(i.querySelectorAll('p')).reduce((total, p) => {
                  return total + p.offsetHeight;
                }, 0);

                let wrapHeight = i.offsetHeight;

                i.style.display = '';
                i.style.visibility = '';
                i.style.position = '';

                if (contentHeight > wrapHeight) {
                  item.style.display = 'block';
                } else {
                  item.style.display = 'none';
                }

                item.addEventListener('click', () => {
                  // console.log(333)
                  if (i.classList.contains('read-more-mode')) {
                    i.classList.remove('read-more-mode');
                    item.innerHTML = window.translations.readMore;
                  } else {
                    i.classList.add('read-more-mode');
                    item.innerHTML = window.translations.readLess;
                  }
                });
              }
            });
          }


          setTimeout(() => {
            if (popupSlider && !popupSlider.classList.contains('slick-initialized')) {
              $(popupSlider).slick({
                infinite: true,
                slidesToShow: 1,
                slidesToScroll: 1,
                centerMode: false,
                dots: true,
                prevArrow: '<button type="button" class="slick-prev slick-arrow">' + slickPrevArrow + '</button>',
                nextArrow: '<button type="button" class="slick-next slick-arrow">' + slickNextArrow + '</button>',
                // prevArrow: '<button type="button" class="slick-prev slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.646444 3.64742C0.451181 3.84269 0.451181 4.15927 0.646444 4.35453L3.82842 7.53651C4.02369 7.73177 4.34027 7.73177 4.53553 7.53651C4.73079 7.34125 4.73079 7.02467 4.53553 6.8294L1.7071 4.00098L4.53553 1.17255C4.73079 0.977287 4.73079 0.660704 4.53553 0.465442C4.34027 0.27018 4.02369 0.27018 3.82842 0.465442L0.646444 3.64742ZM21 3.50098L0.999998 3.50098V4.50098L21 4.50098V3.50098Z" fill="black"/></svg></button>',
                // nextArrow: '<button type="button" class="slick-next slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.3536 4.35355C20.5488 4.15829 20.5488 3.84171 20.3536 3.64645L17.1716 0.464466C16.9763 0.269204 16.6597 0.269204 16.4645 0.464466C16.2692 0.659728 16.2692 0.976311 16.4645 1.17157L19.2929 4L16.4645 6.82843C16.2692 7.02369 16.2692 7.34027 16.4645 7.53553C16.6597 7.7308 16.9763 7.7308 17.1716 7.53553L20.3536 4.35355ZM0 4.5H20V3.5H0V4.5Z" fill="black"/></svg></button>',
                responsive: [
                  {
                    breakpoint: 989,
                    settings: {
                      slidesToShow: 1,
                      arrows: false,
                      centerPadding: '10px'
                    }
                  }
                ]
              });
            }
          }, 900);

          setTimeout(() => {
            overlay.classList.add('hidden');
          }, 1500);
        }
        if (event.target.closest('.products-list__product-atc')) {
          if (event.__atcHandled) return;
          event.__atcHandled = true;
          // console.log('products-list__products');
          const item = event.target.closest('.products-list__product-atc');
          let id = item.dataset.variantid;
          let maxqty = item.dataset.maxqty;
          addToCart(id, 1, maxqty, 'product-list__products');
        }
      });
    });
  }
  if (document.querySelectorAll('.additional-products-cart--items--wrap')) {
    document.querySelectorAll('.additional-products-cart--items--wrap').forEach(function (productList) {
      productList.addEventListener('click', function (event) {
        if (event.target.closest('.quick-view-trigger')) {
          const trigger = event.target.closest('.quick-view-trigger');
          let cart = document.getElementById('mini-cart__wrap');
          let quickViewOverLay = trigger.classList.contains('ajax-cart-trigger');

          // if (quickViewOverLay && cart) {
          if (cart.classList.contains('open')) {
            cart.classList.remove('open');
          }

          event.preventDefault();
          var handle = trigger.getAttribute('data-product-handle');
          let content = document.querySelector('.card-grid-quick-view-content[data-prodid = "' + handle + '"]');
          quickViewPopupWrap.innerHTML = content.innerHTML;
          const overlay = document.createElement('div');
          overlay.classList.add('popup-overlay');
          quickViewPopupWrap.prepend(overlay);
          document.body.classList.add('overflow-hidden');
          document.querySelector('html').classList.add('overflow-hidden');

          const popupSlider = document.querySelector('.quick-view-popup .large-image');

          quickViewPopup.classList.remove('hidden-smooth');

          const readMorePopup = document.querySelectorAll('.quick-view-content .read-more-btn');

          if (readMorePopup) {
            readMorePopup.forEach((item) => {
              let i = item.closest('.product-info__opys');
              if (i) {
                // Temporarily show the element to measure its content
                i.style.display = 'block';
                i.style.visibility = 'hidden';
                i.style.position = 'absolute';

                let contentHeight = Array.from(i.querySelectorAll('p')).reduce((total, p) => {
                  return total + p.offsetHeight;
                }, 0);

                let wrapHeight = i.offsetHeight;

                i.style.display = '';
                i.style.visibility = '';
                i.style.position = '';

                if (contentHeight > wrapHeight) {
                  item.style.display = 'block';
                } else {
                  item.style.display = 'none';
                }

                item.addEventListener('click', () => {
                  // console.log(333)
                  if (i.classList.contains('read-more-mode')) {
                    i.classList.remove('read-more-mode');
                    item.innerHTML = window.translations.readMore;
                  } else {
                    i.classList.add('read-more-mode');
                    item.innerHTML = window.translations.readLess;
                  }
                });
              }
            });
          }


          setTimeout(() => {
            if (popupSlider && !popupSlider.classList.contains('slick-initialized')) {
              $(popupSlider).slick({
                infinite: true,
                slidesToShow: 1,
                slidesToScroll: 1,
                centerMode: false,
                dots: true,
                prevArrow: '<button type="button" class="slick-prev slick-arrow">' + slickPrevArrow + '</button>',
                nextArrow: '<button type="button" class="slick-next slick-arrow">' + slickNextArrow + '</button>',
                // prevArrow: '<button type="button" class="slick-prev slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.646444 3.64742C0.451181 3.84269 0.451181 4.15927 0.646444 4.35453L3.82842 7.53651C4.02369 7.73177 4.34027 7.73177 4.53553 7.53651C4.73079 7.34125 4.73079 7.02467 4.53553 6.8294L1.7071 4.00098L4.53553 1.17255C4.73079 0.977287 4.73079 0.660704 4.53553 0.465442C4.34027 0.27018 4.02369 0.27018 3.82842 0.465442L0.646444 3.64742ZM21 3.50098L0.999998 3.50098V4.50098L21 4.50098V3.50098Z" fill="black"/></svg></button>',
                // nextArrow: '<button type="button" class="slick-next slick-arrow"><svg width="21" height="8" viewBox="0 0 21 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.3536 4.35355C20.5488 4.15829 20.5488 3.84171 20.3536 3.64645L17.1716 0.464466C16.9763 0.269204 16.6597 0.269204 16.4645 0.464466C16.2692 0.659728 16.2692 0.976311 16.4645 1.17157L19.2929 4L16.4645 6.82843C16.2692 7.02369 16.2692 7.34027 16.4645 7.53553C16.6597 7.7308 16.9763 7.7308 17.1716 7.53553L20.3536 4.35355ZM0 4.5H20V3.5H0V4.5Z" fill="black"/></svg></button>',
                responsive: [
                  {
                    breakpoint: 769,
                    settings: {
                      slidesToShow: 1,
                      centerPadding: '10px'
                    }
                  }
                ]
              });
            }
          }, 900);

          setTimeout(() => {
            overlay.classList.add('hidden');
          }, 1500);
        }
        if (event.target.closest('.products-list__product-atc')) {
          // console.log('products-list__products');
          const item = event.target.closest('.products-list__product-atc');
          let id = item.dataset.variantid;
          let maxqty = item.dataset.maxqty;
          addToCart(id, 1, maxqty, 'additional-products-cart--items--wrap');
        }
      });
    });
  }

  if (readMore) {
    readMore.forEach((item) => {
      let i = item.closest('.product-info__opys');
      if (i) {
        i.style.display = 'flex';
        i.style.visibility = 'hidden';
        i.style.position = 'absolute';

        let contentHeight = Array.from(i.querySelectorAll('p')).reduce((total, p) => {
          return total + p.offsetHeight;
        }, 0);

        let wrapHeight = i.offsetHeight;

        i.style.display = '';
        i.style.visibility = '';
        i.style.position = '';

        if (contentHeight > wrapHeight) {
          item.style.display = 'flex';
        } else {
          // console.log(i, "iiiiiiiii", contentHeight > wrapHeight, contentHeight, wrapHeight);
          item.style.display = 'none';
        }

        item.addEventListener('click', () => {
          if (i.className.includes('read-more-mode')) {
            i.classList.remove('read-more-mode');
            item.innerHTML = window.translations.readMore;
            document.querySelector('.custom-product-template #quick-view-title').scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          } else {
            i.classList.add('read-more-mode');
            item.innerHTML = window.translations.readLess;
          }
        });
      }
    });
  }


});
